export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePassword = (password: string): { valid: boolean; message?: string } => {
  if (password.length < 8) {
    return { valid: false, message: 'Password must be at least 8 characters long' };
  }
  if (!/[A-Z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one lowercase letter' };
  }
  if (!/[0-9]/.test(password)) {
    return { valid: false, message: 'Password must contain at least one number' };
  }
  return { valid: true };
};

/** Max digits (E.164) and max total characters for phone inputs */
export const PHONE_MAX_DIGITS = 15;
export const PHONE_MAX_CHARS = 20;

/** Sanitize phone: only 0-9 + - ( ) space; cap at PHONE_MAX_DIGITS digits and PHONE_MAX_CHARS total */
export function sanitizePhone(v: string): string {
  const cleaned = v.replace(/[^0-9+\-() ]/g, '');
  let digitCount = 0;
  let result = '';
  for (const c of cleaned) {
    if (result.length >= PHONE_MAX_CHARS) break;
    if (/\d/.test(c)) {
      if (digitCount >= PHONE_MAX_DIGITS) continue;
      digitCount++;
    }
    result += c;
  }
  return result;
}

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/** Strip non-digits from a string (for admission number etc.). Use in onChange so only digits are stored. */
export const sanitizeAdmissionNumber = (value: string): string => value.replace(/\D/g, '');

/** Strip digits from a string (for person name inputs). Use in onChange to prevent/sanitize digits. */
export const stripDigitsFromName = (value: string): string => value.replace(/[0-9]/g, '');

/** Person name: must not contain digits. Returns { valid, message? }. */
export const validatePersonName = (value: string): { valid: boolean; message?: string } => {
  if (/[0-9]/.test(value)) {
    return { valid: false, message: 'Name should not contain numbers' };
  }
  return { valid: true };
};

/** Grade name: must contain at least one letter (reject only numbers/symbols). */
export const validateGradeName = (value: string): { valid: boolean; message?: string } => {
  const trimmed = value.trim();
  if (!trimmed) return { valid: false, message: 'Name is required' };
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { valid: false, message: 'Name must contain at least one letter' };
  }
  return { valid: true };
};

/** Grade code: must match G1 through G12 (e.g. G1, G2, G10, G12). */
export const validateGradeCode = (value: string): { valid: boolean; message?: string } => {
  const trimmed = value.trim();
  if (!trimmed) return { valid: false, message: 'Code is required' };
  if (!/^G([1-9]|1[0-2])$/i.test(trimmed)) {
    return { valid: false, message: 'Code must be G1 through G12 (e.g. G2, G10)' };
  }
  return { valid: true };
};

export const validateRequired = (value: string | undefined | null): boolean => {
  return value !== undefined && value !== null && value.trim().length > 0;
};

export const validateMinLength = (value: string, minLength: number): boolean => {
  return value.length >= minLength;
};

export const validateMaxLength = (value: string, maxLength: number): boolean => {
  return value.length <= maxLength;
};

